import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, KeyRound, Sparkles, TrendingUp, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import Brand from "../../components/Brand";
import LogoMark from "../../components/LogoMark";

export default function CustomerLogin() {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("phone");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const sendOtp = async () => {
    if (phone.length < 10) return toast.error("Enter a valid 10-digit phone");
    setLoading(true);
    try {
      const r = await api.post("/auth/customer/send-otp", { phone });
      setDevOtp(r.data.dev_otp || "");
      setStage("otp");
      toast.success("OTP sent");
    } catch (_) { toast.error("Failed to send OTP"); }
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setLoading(true);
    try {
      const r = await api.post("/auth/customer/verify-otp", { phone, otp });
      if (r.data.is_new_user) { navigate("/register", { state: { phone } }); return; }
      login(r.data.token, r.data.user);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F4E9]">
      {/* LEFT: decorative panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 60%, #7A1010 100%)" }}>
        {/* Decorative circles */}
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#B8862A]/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-[#3F6B3F]/30 blur-3xl" />
        <div className="absolute top-1/3 right-10 w-3 h-3 rounded-full bg-[#F2C97A]" />
        <div className="absolute top-1/2 right-32 w-2 h-2 rounded-full bg-white/60" />
        <div className="absolute bottom-1/4 left-16 w-4 h-4 rounded-full bg-[#F2C97A]/80" />

        <Link to="/" className="relative inline-flex items-center gap-1 text-sm text-white/80 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-semibold mb-6 border border-white/20">
            <Sparkles className="w-3.5 h-3.5 text-[#F2C97A]" /> AI-powered livestock investing
          </div>
          <h2 className="font-display text-4xl xl:text-5xl leading-tight tracking-tight">
            Own a flock.<br />Earn while it grows.
          </h2>
          <p className="mt-5 text-white/85 text-base leading-relaxed max-w-md">
            Every animal tagged. Every weight tracked. Every rupee accounted for.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-sm">
            <Stat n="3-4mo" l="Young stock" />
            <Stat n="2 mo" l="Lock-in" />
            <Stat n="~25%" l="Avg return" />
          </div>
        </div>

        <div className="relative grid grid-cols-3 gap-3 text-xs">
          <Pill icon={ShieldCheck} text="Verified KYC" />
          <Pill icon={TrendingUp} text="Profit-share" />
          <Pill icon={Sparkles} text="AI insights" />
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex flex-col items-center justify-center p-6 md:p-10 relative">
        <Link to="/" className="lg:hidden absolute top-4 left-4 text-sm text-[#6B5F58] flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <LogoMark size="xl" className="mx-auto mb-3" />
            <p className="text-sm text-[#6B5F58]">Welcome back. Sign in with your mobile.</p>
          </div>

          <div className="gf-card p-7 shadow-xl shadow-[#D71921]/10">
            {stage === "phone" && (
              <div className="space-y-4 animate-in">
                <label className="block">
                  <span className="text-sm font-medium text-[#1F1816]">Mobile number</span>
                  <div className="mt-2 relative">
                    <Phone className="w-4 h-4 absolute left-3 top-3.5 text-[#6B5F58]" />
                    <input data-testid="login-phone-input" autoFocus type="tel" maxLength={10}
                      className="w-full rounded-xl border border-[#E8E0D2] pl-9 pr-3 py-3 text-base focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15 outline-none"
                      placeholder="9876543210" value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} />
                  </div>
                </label>
                <button data-testid="login-send-otp-btn" onClick={sendOtp} disabled={loading}
                  className="w-full px-6 py-3.5 rounded-xl text-white font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 shadow-lg shadow-[#D71921]/25"
                  style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                  {loading ? "Sending..." : "Send OTP"}
                </button>
                <div className="relative my-4 text-center text-xs text-[#6B5F58]">
                  <span className="px-3 bg-white relative z-10">or</span>
                  <div className="absolute inset-x-0 top-1/2 h-px bg-[#E8E0D2] -z-0" />
                </div>
                <Link to="/register" data-testid="login-register-link"
                  className="block w-full text-center px-6 py-3 rounded-xl border-2 border-[#E8E0D2] hover:border-[#D71921] hover:text-[#D71921] font-medium transition-colors">
                  Create new account
                </Link>
              </div>
            )}
            {stage === "otp" && (
              <div className="space-y-4 animate-in">
                <div className="text-sm text-[#6B5F58]">OTP sent to <b className="text-[#1F1816]">{phone}</b></div>
                {devOtp && (
                  <div className="text-xs bg-gradient-to-r from-[#FCEDED] to-[#FFF5DA] border border-[#F2C97A] text-[#7A1010] rounded-lg p-3 font-mono" data-testid="dev-otp-hint">
                    🛠 Dev OTP: <b>{devOtp}</b>
                  </div>
                )}
                <label className="block">
                  <span className="text-sm font-medium">Enter 6-digit OTP</span>
                  <div className="mt-2 relative">
                    <KeyRound className="w-4 h-4 absolute left-3 top-3.5 text-[#6B5F58]" />
                    <input data-testid="login-otp-input" autoFocus type="text" maxLength={6}
                      className="w-full rounded-xl border border-[#E8E0D2] pl-9 pr-3 py-3 text-base font-mono tracking-[0.4em] focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15 outline-none"
                      placeholder="000000" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
                  </div>
                </label>
                <button data-testid="login-verify-otp-btn" onClick={verifyOtp} disabled={loading}
                  className="w-full px-6 py-3.5 rounded-xl text-white font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 shadow-lg shadow-[#D71921]/25"
                  style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                  {loading ? "Verifying..." : "Verify & Continue"}
                </button>
                <button onClick={() => setStage("phone")} className="w-full text-xs text-[#6B5F58] hover:text-[#1F1816]">
                  Change number
                </button>
              </div>
            )}
          </div>

          <div className="text-center mt-6 text-xs text-[#6B5F58]">
            Are you an admin? <Link to="/admin/login" className="text-[#D71921] font-medium underline">Admin Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-3 border border-white/15">
      <div className="font-display text-lg leading-tight">{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/70 mt-0.5">{l}</div>
    </div>
  );
}
function Pill({ icon: Icon, text }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-full px-3 py-2 border border-white/15 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-[#F2C97A]" /> {text}
    </div>
  );
}
